import type { KeyboardEvent, MouseEvent } from 'react';

/**
 * Stops a portalled overlay's events reaching whatever it was opened from.
 *
 * Radix overlays (Dialog, Sheet, Popover, DropdownMenu, Select) render into
 * `<body>`, so in the DOM they sit nowhere near their trigger. React, however,
 * dispatches synthetic events along the REACT tree — and in the React tree the
 * panel is still a child of the row/card that opened it. A `DataTable` row with
 * `onRowClick` therefore fired on every click inside the overlay: opening the
 * barcode dialog on a Purchase Order and choosing "QR code only" navigated
 * straight to the record instead of switching the code type.
 *
 * Containment is deliberately narrow on the keyboard side. `DataTable` rows
 * activate on Enter and Space only, so those are the only keys swallowed —
 * Escape must keep bubbling or Radix can no longer close the overlay, and Tab
 * must keep bubbling or focus can no longer cycle.
 *
 * Usage: spread onto the overlay's Content, after destructuring the caller's
 * own handlers so both still run.
 *
 *   const contain = portalContainment(onClick, onKeyDown);
 *   <Primitive.Content {...contain} {...props} />
 */
export function portalContainment<E extends HTMLElement>(
  onClick?: (event: MouseEvent<E>) => void,
  onKeyDown?: (event: KeyboardEvent<E>) => void,
) {
  return {
    onClick: (event: MouseEvent<E>) => {
      onClick?.(event);
      event.stopPropagation();
    },
    onKeyDown: (event: KeyboardEvent<E>) => {
      onKeyDown?.(event);
      if (event.key === 'Enter' || event.key === ' ') {
        event.stopPropagation();
      }
    },
  };
}
